import type { Material, Object3D } from "three";

export interface ModelMaterialSlot {
  readonly id: string;
  readonly label?: string;
  readonly material?: Material;
}

/**
 * Result of a catalog-code driven material change. Mirrors the public
 * `sugar-model-viewer-material-applied` event detail so the viewer contract can
 * be forwarded verbatim (checked by `npm run check:platform-contracts`).
 */
export interface MaterialApplyResult {
  ok: boolean;
  groupCode: string;
  materialCode: string;
  error?: string;
}

/** A catalog material group (`SugarPartGroup`-shaped) exposed by the port. */
export interface ModelMaterialGroup {
  readonly id: string;
  readonly code: string;
  readonly name?: string;
  readonly selectedMaterialCode?: string;
}

/**
 * Optional material capability exposed by a loaded model. Products that do not
 * support material editing can omit the port entirely.
 *
 * `list`/`apply` work on raw three.js materials (uuid keyed). `listGroups`/
 * `applyByCode` are the catalog-level surface the product UIs actually drive;
 * they are optional because not every model kind has catalog groups.
 */
export interface ModelMaterialPort {
  list(): readonly ModelMaterialSlot[];
  apply(slotId: string, material: Material): void | Promise<void>;
  listGroups?(): readonly ModelMaterialGroup[];
  applyByCode?(
    groupCode: string,
    materialCode: string,
  ): Promise<MaterialApplyResult>;
  invalidate?(): void;
}

export class ObjectMaterialPort implements ModelMaterialPort {
  constructor(private readonly root: Object3D) {}

  list(): readonly ModelMaterialSlot[] {
    const slots: ModelMaterialSlot[] = [];
    this.root.traverse((object) => {
      const material = (object as { material?: Material | Material[] }).material;
      const materials = Array.isArray(material) ? material : material ? [material] : [];
      for (const candidate of materials) {
        if (!slots.some((slot) => slot.id === candidate.uuid)) {
          slots.push({
            id: candidate.uuid,
            label: candidate.name || undefined,
            material: candidate,
          });
        }
      }
    });
    return slots;
  }

  apply(slotId: string, replacement: Material): void {
    this.root.traverse((object) => {
      const renderable = object as { material?: Material | Material[] };
      if (Array.isArray(renderable.material)) {
        renderable.material = renderable.material.map((material) =>
          material.uuid === slotId ? replacement : material,
        );
      } else if (renderable.material?.uuid === slotId) {
        renderable.material = replacement;
      }
    });
  }
}
